import { Button as HeadlessUIButton } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import type { RefObject } from "hono/jsx";
import type { ForwardedRef, HTMLAttributes } from "react";
import { Spinner, type SpinnerProps } from "../spinner";
import { StatusIndicator } from "../status-indicator";

export interface BaseButtonProps
	extends Omit<VariantProps<typeof button>, "isIconButton">,
		HTMLAttributes<HTMLButtonElement> {
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
	type?: "button" | "submit" | "reset";
	icon?: React.ReactNode;
	autoFocus?: boolean;
	stopPropagation?: boolean;
	withPing?: boolean;
	ref?: RefObject<HTMLButtonElement> | ForwardedRef<HTMLButtonElement>;
}

type ButtonProps =
	| (BaseButtonProps & { icon: React.ReactNode; children?: React.ReactNode })
	| (BaseButtonProps & { icon?: never; children: React.ReactNode });

export function Button({
	children,
	onClick,
	type = "button",
	variant = "white",
	size: _size,
	isLoading,
	isDisabled,
	className,
	icon,
	iconPosition = "left",
	autoFocus,
	stopPropagation,
	withPing,
	ref,
	...props
}: ButtonProps) {
	/** Hooks */
	const isIconButton = Boolean(icon && !children);
	const size = _size || "xs";

	/** Classes */
	const classes = button({
		className,
		iconPosition,
		isDisabled,
		isIconButton,
		isLoading,
		size,
		variant,
	});

	const buttonIconClasses = buttonIcon({
		variant,
		isIconButton,
		isLoading,
	});

	/** Render */
	return (
		<HeadlessUIButton
			ref={ref}
			className={clsx(classes, className)}
			type={type}
			onClick={(e) => {
				if (onClick) {
					if (stopPropagation) e.stopPropagation();
					onClick(e);
				}
			}}
			disabled={!!isLoading || !!isDisabled}
			autoFocus={autoFocus}
			{...props}
		>
			{withPing && (
				<div className="absolute -top-1 -right-1">
					<StatusIndicator
						size="md"
						color="accent"
						withPing
					/>
				</div>
			)}
			{icon ? <span className={buttonIconClasses}>{icon}</span> : null}
			{isLoading && (
				<div className="absolute inset-0 flex justify-center items-center">
					<Spinner color={getSpinnerVariant(variant)} />
				</div>
			)}
			{children ? (
				<span className={clsx(isLoading ? "opacity-0" : "opacity-100", "truncate")}>
					{children}
				</span>
			) : null}
		</HeadlessUIButton>
	);
}

const button = cva(
	[
		"cursor-pointer",
		"shrink-0",
		"rounded-base relative font-medium",
		"flex flex-row items-center justify-center",
		"gap-2",
		"bg-gradient-to-b",
		"active:opacity-95 transition-all",
		"hover:opacity-90",
		"shadow-xs",
		"outline-none",
		"focus:ring focus:ring-offset-2 focus:ring-accent-500",
		"active:translate-y-[1px]",
		"relative",
	],
	{
		variants: {
			iconPosition: {
				left: ["flex-row"],
				right: ["flex-row-reverse"],
			},
			isDisabled: {
				false: [],
				true: ["!cursor-not-allowed", "!opacity-50"],
			},
			isIconButton: {
				false: [],
				true: ["!px-[unset] aspect-square"],
			},
			isLoading: {
				false: [],
				true: ["!cursor-not-allowed"],
			},
			size: {
				md: ["text-base px-5 h-12"],
				sm: ["text-sm px-4 h-9.5"],
				xs: ["text-xs px-3 h-8"],
				xxs: ["text-xs px-2 h-7"],
			},
			variant: {
				accent: [
					"text-white",
					"border-[0.5px] border-accent-500 dark:border-accent-400",
					"bg-accent-600 dark:bg-accent-600",
				],
				danger: [
					"text-white",
					"border-transparent",
					"bg-radial-[at_30%_0%]",
					"from-red-300 to-red-500",
					"dark:from-red-300 dark:to-red-400",
				],
				"danger-ghost": [
					"text-red-500 dark:text-red-500",
					"hover:bg-red-100",
					"dark:hover:bg-red-900/30 dark:active:bg-red-900/10",
					"!shadow-none",
				],
				"success-ghost": [
					"text-emerald-600 dark:text-emerald-400",
					"hover:bg-emerald-100",
					"dark:hover:bg-emerald-900/30 dark:active:bg-emerald-900/10",
					"!shadow-none",
				],
				ghost: [
					"text-primaryGray-700 dark:text-white",
					"hover:bg-primaryGray-100",
					"dark:hover:bg-primaryGray-800 dark:active:bg-primaryGray-800/60",
					"!shadow-none",
				],
				gray: [
					"text-white",
					"border-transparent",
					"bg-primaryGray-900",
					"dark:from-primaryGray-700 dark:to-primaryGray-900",
				],
				plain: ["!px-0 !py-0 !bg-transparent !shadow-none !border-0 !h-max"],
				primary: [
					"text-white dark:**:text-white!",
					"border-0 dark:border-[0.5px] border-transparent dark:border-color",
					"bg-primary-700 dark:bg-primary-950! dark:**:text-black!",
				],
				success: ["text-white", "border-transparent", "bg-emerald-500"],
				white: [
					"border-[0.5px] border-color",
					"text-black dark:text-white",
					"bg-white dark:bg-primaryGray-900",
				],
			},
		},
	},
);

const buttonIcon = cva([], {
	variants: {
		variant: {
			primary: ["text-white"],
			danger: ["text-white"],
			"danger-ghost": ["text-red-500"],
			"success-ghost": ["text-emerald-600 dark:text-emerald-400"],
			accent: ["text-white"],
			white: ["text-primaryGray-500"],
			gray: ["text-primaryGray-500"],
			ghost: ["text-primaryGray-500"],
			plain: ["text-primaryGray-500"],
			success: ["text-white"],
		},
		isIconButton: {
			true: [""],
			false: [],
		},
		isLoading: {
			true: ["opacity-0"],
			false: ["opacity-100"],
		},
	},
	compoundVariants: [
		{
			isIconButton: true,
			variant: "primary",
			class: "",
		},
		{
			isIconButton: true,
			variant: "danger",
			class: "",
		},
		{
			isIconButton: true,
			variant: "danger-ghost",
			class: "",
		},
		{
			isIconButton: true,
			variant: "success-ghost",
			class: "",
		},
		{
			isIconButton: true,
			variant: "accent",
			class: "",
		},
		{
			isIconButton: true,
			variant: "white",
			class: "",
		},
		{
			isIconButton: true,
			variant: "gray",
			class: "",
		},
		{
			isIconButton: true,
			variant: "ghost",
			class: "",
		},
		{
			isIconButton: true,
			variant: "plain",
			class: "",
		},
	],
});

function getSpinnerVariant(
	variant: Pick<VariantProps<typeof button>, "variant">["variant"],
): SpinnerProps["color"] {
	switch (variant) {
		case "primary":
			return "white";
		case "white":
			return "primary";
		case "gray":
			return "primary";
		case "ghost":
			return "primary";
		case "danger":
			return "white";
		case "accent":
			return "white";
		case "plain":
			return "primary";
		case "danger-ghost":
			return "primary";
		case "success-ghost":
			return "primary";
		case "success":
			return "white";
	}
}
